import { DEMO_SHOP_SLUG, THEME_DEMO_SHOPS, demoShopSlug, isDemoShopSlug } from '@xshop/shared';
import { isThemeSlug } from '../themeHost/themeMount';

export { DEMO_SHOP_SLUG, demoShopSlug, isDemoShopSlug };

/** The demo store's own written page, so previewing a theme's content page shows a real document. */
export const DEMO_CONTENT_SLUG = 'terms-and-conditions';

/**
 * The host suffixes on which a store answers under its own slug — the twin of BUILTIN_HOST_SUFFIXES
 * in backend/config/storefront-host.ts. Kept here because the demo shop's address is spelled by
 * reading a hostname against them, and client.ts reads it through this module.
 */
export const STORE_HOST_SUFFIXES = ['.xshop.ma', '.localhost'];

/**
 * Which shop a theme is drawn against, before and after it is installed — the whole of the rule, in
 * one place.
 *
 * A merchant judging a theme cannot judge an empty grid, so everything they see of it before they
 * install it is drawn against a seeded demo store: a real tenant in the database with a full
 * catalogue, real photographs, prices, reviews and stock. It is fixture data for a fictional
 * merchant, held in `backend/packages/database/src/seed-demo-shop.ts` (and `seed-theme-demos.ts`
 * for the store of its own niche each production theme is drawn against) and never in a theme file —
 * no theme ships a product. Which of those stores a preview reads is `demoShopSlug(theme)` and nothing else.
 *
 * The switch is the address naming a demo shop and carrying `?theme=`, and nothing else. The slug
 * subdomain alone names the demo shop; `?theme=` alone would let any merchant's address wear the
 * demo catalogue, which is why a preview needs both. An installed storefront serves its own theme at
 * its own address with no query at all. So there is exactly one branch that reads a demo store,
 * reachable only from a demo shop's own address, and demo content has no path into a real shop.
 */

// Reports whether this hostname names one of the seeded demo shops.
export function isDemoShopHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  const suffix = STORE_HOST_SUFFIXES.find((candidate) => host.endsWith(candidate) && host.length > candidate.length);
  return suffix !== undefined && isDemoShopSlug(host.slice(0, -suffix.length));
}

// Reads the theme this visit asked to try on, or null when the visit is not a preview.
export function previewThemeSlug(search: string, _hostname?: string): string | null {
  const asked = new URLSearchParams(search).get('theme');
  return isThemeSlug(asked) ? asked : null;
}

// Reports whether this visit is a theme preview.
export function isThemePreview(search?: string, hostname?: string): boolean {
  const query = search ?? (typeof window === 'undefined' ? '' : window.location.search);
  return previewThemeSlug(query, hostname) !== null;
}

// Reports whether this visit previews a theme on the shared demo store, whose catalogue is not the theme's own, so its
// grids and product blocks draw the theme's samples; a theme with a demo store of its own niche shows that store's products.
export function previewShowsSamples(search?: string, hostname?: string): boolean {
  const query = search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const theme = previewThemeSlug(query, hostname);
  return theme !== null && !(theme in THEME_DEMO_SHOPS);
}

// Names the demo store this preview reads, or null when the visit is not a preview at all.
export function previewShopSlug(search?: string, hostname?: string): string | null {
  const query = search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const theme = previewThemeSlug(query, hostname);
  return theme === null ? null : demoShopSlug(theme);
}

/**
 * Keeps a preview out of the index. A preview address shows the demo shop, so an indexed one would
 * put fixture data in a search result as if it were a real catalogue.
 */
export function markPreviewNoIndex(): void {
  if (document.querySelector('meta[name="robots"][data-preview]')) return;
  const meta = document.createElement('meta');
  meta.name = 'robots';
  meta.content = 'noindex, nofollow';
  meta.dataset.preview = 'true';
  document.head.append(meta);
}
